<template>
  <b-container>
    <header class="header pt-4" style="align-items: center; justify-content: flex-start">
      <!-- datetime picker -->
      <StyledDateTimePicker
        class="header__search"
        v-model="dateRange"
        has-range
        has-shortcuts
        placeholder="選擇日期"
        style="left: 10px;"
      ></StyledDateTimePicker>

      <!-- vertical divider -->
      <div
        class="header__search mx-2 mx-md-4"
        style="height: 2rem; border-right: 1px solid #BCC9D1">
      </div>

      <!-- search bar -->
      <div class="header__search search-bar" >
        <img
          src="@/assets/img/search.svg"
          class="mr-2"
          alt="search"
        />
        <input
          v-model.trim="keyword"
          type="text"
          style="flex: 1; border: 0; outline: 0; font-size: 0.875rem"
          size="1"
          placeholder="搜尋"
          aria-label="搜尋"
          aria-describedby="button-addon1"
        />
        <img
          v-if="keyword !== ''"
          @click="keyword = ''"
          src="@/assets/img/close.svg"
          style="height: 0.875rem; cursor: pointer"
          alt="clear"
        />
      </div>
    </header>
    <OverviewList
      :overviews="overviews"
      :loading="loading"
      is-qr-code-overview
    >
    </OverviewList>
    <TheListingTitle
      title="紀錄列表"
      :selectedKeywordAndFilters="selectedKeywordAndFilters"
      @reset-click="initialize"
    >
    </TheListingTitle>
    <section class="table-content">
      <template>
        <BaseFilterToolbar @reset="initialize" :loading="loading">
          <div class="d-flex">
            <label id="keyword-filter">
              <input
                v-model.trim="keyword"
                type="text"
                class="form-control"
                placeholder="關鍵字..."
                aria-label="關鍵字..."
                aria-describedby="button-addon1"
              />
            </label>
          </div>
        </BaseFilterToolbar>
        <DataTable
          v-show="list"
          :fields="fields"
          :mobileFields="mobileFields"
          :items="list"
          :total-result="total"
          :options.sync="options"
          :loading="loading"
          showRowDetails
        >
          <template v-slot:cell(status)="{ value }">
            <StatusLabel :item="value"></StatusLabel>
          </template>
          <template v-slot:cell(rewardName)="{ item }">

            <div class="d-flex">
              <img v-if="item.photo" class="data-table__cover mr-2"
              :src="item.photo">
              <div>
                {{ item.rewardName }}
                <br>
                <div class="text-secondary" style="font-size: 0.8rem;">{{ item.ref }}</div>
              </div>
            </div>
          </template>
        </DataTable>
        <b-skeleton-table v-if="!list" :rows="15" :columns="5"></b-skeleton-table>
      </template>
    </section>
  </b-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { scheduled, asyncScheduler } from 'rxjs';
import {
  tap, map, switchMap, debounceTime, distinctUntilChanged, pluck, mergeAll,
} from 'rxjs/operators';
import {
  BContainer, BSkeletonTable,
} from 'bootstrap-vue';
import isEqual from 'lodash/isEqual';
import { getPaymentCouponStatus } from '@/helpers/coupon-status';
import StyledDateTimePicker from '@/components/qr-scanner/StyledDateTimePicker.vue';
import formatCurrency from '@/helpers/currency';
import dataTableMixin from '../../mixins/data-table';
import filtersQueryStringMixin from '../../mixins/optionsQueryString';
import optionsQueryStringMixin from '../../mixins/filtersQueryString';
import TheListingTitle from '../../components/TheListingTitle.vue';
import OverviewList from '../../components/overview/OverviewList.vue';

import DataTable from '../../components/DataTable.vue';
import StatusLabel from '../../components/StatusLabel.vue';
import imagePath from '../../filters/image-path';
import BaseFilterToolbar from '../../components/BaseFilterToolbar.vue';

const MERCHANT_HEADERS = [
  { key: 'rewardName', label: '優惠名稱', sortable: true },
  {
    key: 'ref',
    label: '訂單編號',
    sortable: true,
    formatter: (value) => (value ?? 'N/A (積分優惠券)'),
  },
  { key: 'code', label: '優惠碼', sortable: true },
  { key: 'quantity', label: '購買數量', sortable: true },
  {
    key: 'totalPrice',
    label: '訂單價值',
    sortable: true,
    formatter: (value) => (value !== null ? `HK$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : 'N/A (積分優惠券)'),
  },
  { key: 'paymentTime', label: '交易時間', sortable: true },
  { key: 'redeemTime', label: '使用日期', sortable: true },
  {
    key: 'status',
    label: '狀態',
    sortable: true,
    formatter: (value) => getPaymentCouponStatus(value),
  },
];

const MOBILE_HEADER = [
  {
    key: 'rewardName',
    label: '優惠名稱',
  },
  {
    key: 'status',
    formatter: (value, key, item) => getPaymentCouponStatus(item.status),
    thStyle: {
      display: 'none',
    },
  },
];

export default {
  mixins: [dataTableMixin, filtersQueryStringMixin, optionsQueryStringMixin],
  components: {
    BContainer,
    BSkeletonTable,
    TheListingTitle,
    OverviewList,
    DataTable,
    StatusLabel,
    StyledDateTimePicker,
    BaseFilterToolbar,
  },
  data: () => ({
    filter: {
      keywordType: 'rewardName',
      status: undefined,
    },
    options: {
      sortBy: 'redemptionId',
    },
    keyword: '',
    keywordSelectOptions: [
      { value: 'all', text: '所有關鍵字' },
      { value: 'ref', text: '訂單編號' },
      { value: 'rewardName', text: '優惠名稱' },
      { value: 'rewardId', text: '商品編號' },
      { value: 'merchantId', text: '商戶編號', roles: ['admin', 'sale'] },
      { value: 'merchantName', text: '商戶名稱', roles: ['admin', 'sale'] },
      { value: 'userId', text: '會員編號', adminOnly: true },
      { value: 'userName', text: '會員名稱', adminOnly: true },
      { value: 'brandName', text: '品牌名稱', roles: ['admin', 'sale'] },
    ],
    statusSelectOptions: [
      { value: undefined, text: '所有狀態' },
      { value: 'REDEEMED', text: '已完成' },
      { value: 'REFUND_PENDING', text: '退款中' },
      { value: 'REFUND_FINISHED', text: '已退款' },
    ],
    totalSalesAmount: 0,
    dateRange: null,
    selected: [],
    oldStatusSelected: '',
    excelName: '',
    mobileFields: MOBILE_HEADER,
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    fields() {
      return MERCHANT_HEADERS;
    },
  },
  filters: {
    imagePath,
  },
  subscriptions() {
    const getData$ = (data) => this.httpRequest$({
      method: 'post',
      url: '/agc-sub-merchant-api/qrcode/listScannedCoupons',
    }, data);

    const getStat$ = (data) => this.httpRequest$({
      method: 'post',
      url: '/agc-sub-merchant-api/qrcode/stockCardForSubMerchant',
    }, data);

    const onDateRangeUpdate$ = this.$watchAsObservable('dateRange').pipe(
      pluck('newValue'),
      tap((dateRange) => {
        if (dateRange) {
          [this.filter.startDate, this.filter.endDate] = dateRange;
        } else {
          this.filter.startDate = null;
          this.filter.endDate = null;
        }
      }),
    );

    return {
      list: scheduled(
        [...this.scheduleTasks, onDateRangeUpdate$],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        distinctUntilChanged(isEqual),
        pluck('data'),
        switchMap(getData$),
        map((list) => list.map((item) => {
          // mobile issue only: fix image keep rerender if the imagePath is put in the img tag
          item.photo = imagePath(item.photo, 'couponCover');
          return item;
        })),
      ),
      overviews: scheduled(
        [...this.scheduleTasks, onDateRangeUpdate$],
        asyncScheduler,
      ).pipe(
        mergeAll(),
        tap(() => {
          this.loading = true;
        }),
        debounceTime(250),
        map(() => ({
          data: {
            ...this.filter,
            q: this.keyword,
          },
          options: this.options,
        })),
        distinctUntilChanged(isEqual),
        pluck('data'),

        switchMap(getStat$),
      ),
    };
  },
  methods: {
    initialize() {
      this.filter.keywordType = 'rewardName';
      this.keyword = '';
      this.filter.status = undefined;
      this.dateRange = null;
      setTimeout(() => {
        this.loading = false;
      }, 550);
      // when user updates filter too fast, the loading state will not set back to false
      // this time must be bigger than debounceTime's time
    },
    formatCurrency,
  },
};
</script>

<style scoped lang="scss">
@import '../../assets/scss/data-table/list-table-content';
@import '../../assets/scss/listing/header';

.search-bar {
  display: flex;
  align-items: center;
  min-width: 24rem;
  padding: 0.25rem 1rem;
  background-color: white;
  border: 1px solid #DDE1E5;
  border-radius: 9999px;

  @include respond(tab-landscape) {
    flex: 1;
    min-width: 0;
  }
}

.data-table {
  &::v-deep table thead th:first-of-type {
    padding-left: 3.5rem !important;
  }
}
</style>
